/**
 * Data Upload Types
 *
 * Types for the data upload batch management system.
 * Mirrors the Firestore batch document + pipeline API responses.
 */

// ============================================================================
// ENTITY TYPES
// ============================================================================

export type EntityType =
  | 'cooperativas_s1'
  | 'cooperativas_s2'
  | 'cooperativas_s3'
  | 'mutualistas'
  | 'bancos_privados';

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  cooperativas_s1: 'Cooperativas Segmento 1',
  cooperativas_s2: 'Cooperativas Segmento 2',
  cooperativas_s3: 'Cooperativas Segmento 3',
  mutualistas: 'Mutualistas',
  bancos_privados: 'Bancos Privados',
};

// ============================================================================
// BATCH STATUS STATE MACHINE
// ============================================================================

export type BatchStatus =
  | 'preflight_review'
  | 'uploaded'
  | 'stage1_queued'
  | 'stage1_running'
  | 'stage1_complete'
  | 'stage1_failed'
  | 'stage2_queued'
  | 'stage2_running'
  | 'stage2_complete'
  | 'stage2_failed'
  | 'stage3_queued'
  | 'stage3_running'
  | 'stage3_complete'
  | 'stage3_failed';

export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  preflight_review: 'Preflight Review',
  uploaded: 'Uploaded',
  stage1_queued: 'Stage 1 Queued',
  stage1_running: 'Stage 1 Running',
  stage1_complete: 'Stage 1 Complete',
  stage1_failed: 'Stage 1 Failed',
  stage2_queued: 'Stage 2 Queued',
  stage2_running: 'Stage 2 Running',
  stage2_complete: 'Stage 2 Complete',
  stage2_failed: 'Stage 2 Failed',
  stage3_queued: 'Stage 3 Queued',
  stage3_running: 'Stage 3 Running',
  stage3_complete: 'Stage 3 Complete',
  stage3_failed: 'Stage 3 Failed',
};

// ============================================================================
// FILE TYPES
// ============================================================================

export type UploadFileType = 'balance_sheet' | 'patrimonio_tecnico' | 'unknown';

export interface BatchFile {
  filename: string;
  file_type: UploadFileType;
  gcs_path: string;
  size_bytes: number;
  content_hash: string;
  row_count: number;
}

// ============================================================================
// PRE-FLIGHT ANALYSIS
// ============================================================================

export interface PreflightAlert {
  tipo: 'advertencia' | 'error' | 'info';
  mensaje: string;
}

export interface InstitutionCrossRef {
  matched_count: number;
  unmatched_count: number;
  total_sampled: number;
  matched_names: string[];
  unmatched_rucs: string[];
}

export interface ComputedStats {
  delimiter: string;
  decimal_separator: 'comma' | 'dot' | 'unknown';
  declared_period_found: boolean;
  periods_found: string[];
  unique_rucs: number;
  unique_cuentas: number;
  non_zero_saldo_count: number;
  total_sampled_rows: number;
  sample_positions: string[];
  expected_format: {
    delimiter?: string;
    decimal?: string;
    encoding?: string;
  };
  institution_cross_ref?: InstitutionCrossRef;
}

export interface PreflightAnalysis {
  resumen: {
    total_filas: number;
    periodos_encontrados: string[];
    instituciones_count: number;
    codigos_cuenta_count: number;
  };
  calidad: {
    completitud: 'alta' | 'media' | 'baja';
    completitud_detalle: string;
    consistencia: 'alta' | 'media' | 'baja';
    consistencia_detalle: string;
  };
  alertas: PreflightAlert[];
  recomendacion: {
    proceder: boolean;
    mensaje: string;
  };
  computed_stats?: ComputedStats;
}

// ============================================================================
// BATCH DOCUMENT
// ============================================================================

export interface UploadBatch {
  batch_id: string;
  status: BatchStatus;
  entity_type: EntityType;
  period: string;
  uploaded_by: string;
  uploaded_by_email: string;
  uploaded_at: string | { _seconds: number; _nanoseconds: number };
  files: BatchFile[];
  // Pre-flight analysis
  preflight_analysis: PreflightAnalysis | null;
  preflight_analyzed_at: string | { _seconds: number; _nanoseconds: number } | null;
  rating_entity_id: string;
  risk_model_id: string;
  // Stage 1
  stage1_job_id: string | null;
  stage1_started_at: string | null;
  stage1_completed_at: string | null;
  stage1_error: string | null;
  stage1_rows_loaded: number | null;
  // Stage 2
  stage2_job_id: string | null;
  stage2_started_at: string | null;
  stage2_completed_at: string | null;
  stage2_error: string | null;
  stage2_indicators_calculated: number | null;
  // Stage 3
  stage3_job_id: string | null;
  stage3_started_at: string | null;
  stage3_completed_at: string | null;
  stage3_error: string | null;
  stage3_ratings_calculated: number | null;
}

// ============================================================================
// API RESPONSES
// ============================================================================

export interface ListBatchesResponse {
  batches: UploadBatch[];
  count: number;
}

export interface StageTriggerResponse {
  message: string;
  executionId: string;
}

export interface DeleteBatchResponse {
  success: boolean;
  message: string;
}

export interface StageStatus {
  jobId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
  rowsLoaded?: number | null;
  indicatorsCalculated?: number | null;
  ratingsCalculated?: number | null;
}

export interface JobStatusResponse {
  batchId: string;
  status: BatchStatus;
  cachesCleared?: boolean;
  stages: {
    stage1: StageStatus;
    stage2: StageStatus;
    stage3: StageStatus;
  };
}

export interface BatchResultsResponse {
  batchId: string;
  period: string;
  entityType: string;
  totalRatings: number;
  distribution: Record<string, number>;
  stage1RowsLoaded: number | null;
  stage2IndicatorsCalculated: number | null;
  stage3RatingsCalculated: number | null;
}

export interface CheckPeriodResponse {
  period: string;
  hasExistingData: boolean;
  ratingsCount: number;
}

export interface ClearCachesResponse {
  cleared: Record<string, number>;
  message: string;
}

// ============================================================================
// SIGNED URL UPLOAD (for files > 32MB, bypassing Cloud Run proxy)
// ============================================================================

export interface InitUploadFile {
  name: string;
  size: number;
  type: string;
}

export interface InitUploadResponse {
  batchId: string;
  entityType: EntityType;
  period: string;
  ratingEntityId?: string;
  riskModelId?: string;
  uploads: Array<{
    name: string;
    signedUrl: string;
    gcsPath: string;
    contentType: string;
  }>;
}

export interface FinalizeFile {
  name: string;
  gcsPath: string;
  contentType: string;
}
